/**
 * SSOT for hotel search occupancy display on result cards.
 * Display must reflect original search criteria (roomInfo / searchPhrase), not partial hotel rows.
 */

const ADULTS = "adults";
const KIDS = "kids";
const KIDS_AGES = "kidsAges";
const INFANTS = "infants";

const DEV =
  (typeof import.meta !== "undefined" && import.meta?.env?.DEV) ||
  (typeof process !== "undefined" && process?.env?.NODE_ENV === "development");

function emitTrace(tag, payload) {
  if (!DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(tag, payload);
  } catch {
    /* ignore */
  }
}

function finiteNonNegative(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Normalize one room row from any legacy/supplier shape.
 * @param {object|null|undefined} room
 */
export function normalizeHotelSearchRoomOccupancy(room) {
  if (!room || typeof room !== "object") {
    return { adults: 0, children: 0, infants: 0, childrenAges: [] };
  }

  const adultsRaw =
    room.AdultsCount ??
    room.adults ??
    room.Adults ??
    room.adultscount ??
    room.adults_count ??
    room[ADULTS];

  const kidsAgesRaw =
    room.KidsAges ??
    room.kidsAges ??
    room.childrenAges ??
    room.children_ages ??
    room[KIDS_AGES];

  let childrenAges = Array.isArray(kidsAgesRaw)
    ? kidsAgesRaw.map((age) => Number(age)).filter((age) => Number.isFinite(age))
    : [];

  const kidsCountRaw =
    room.kids ??
    room.Kids ??
    room.children ??
    room.childrenCount ??
    room[KIDS];

  let children = childrenAges.length;
  if (children === 0) {
    const parsedKids = finiteNonNegative(kidsCountRaw);
    if (parsedKids != null) children = parsedKids;
  }

  const adultsParsed = finiteNonNegative(adultsRaw);
  let adults = adultsParsed != null ? adultsParsed : 0;

  // Never treat children as adults; never infer adults from passenger totals.
  if (adults === 0 && children > 0 && adultsParsed == null) {
    adults = 0;
  }

  const infants =
    finiteNonNegative(room.InfantsCount ?? room.infants ?? room[INFANTS]) ?? 0;

  return { adults, children, infants, childrenAges };
}

function sumRoomsOccupancy(rooms) {
  const source = Array.isArray(rooms) ? rooms : [];
  let adults = 0;
  let children = 0;
  let infants = 0;
  const childrenAges = [];

  source.forEach((room) => {
    const normalized = normalizeHotelSearchRoomOccupancy(room);
    adults += normalized.adults;
    children += normalized.children;
    infants += normalized.infants;
    childrenAges.push(...normalized.childrenAges);
  });

  return {
    rooms: source.length,
    adults,
    children,
    infants,
    childrenAges,
    passengers: adults + children + infants,
  };
}

function pickRoomsSource(searchState, searchPayload) {
  const roomInfoRooms = searchState?.roomInfo?.rooms;
  if (Array.isArray(roomInfoRooms) && roomInfoRooms.length > 0) {
    return { rooms: roomInfoRooms, sourceUsedForDisplay: "searchState.roomInfo.rooms" };
  }

  const phraseRooms = searchPayload?.searchPhrase?.rooms;
  if (Array.isArray(phraseRooms) && phraseRooms.length > 0) {
    return { rooms: phraseRooms, sourceUsedForDisplay: "searchPayload.searchPhrase.rooms" };
  }

  const payloadRooms = searchPayload?.rooms;
  if (Array.isArray(payloadRooms) && payloadRooms.length > 0) {
    return { rooms: payloadRooms, sourceUsedForDisplay: "searchPayload.rooms" };
  }

  return { rooms: [], sourceUsedForDisplay: "none" };
}

function computeNights(searchPayload, searchState) {
  const checkIn =
    searchState?.calendarDateArrival ??
    searchPayload?.checkIn ??
    searchPayload?.searchPhrase?.checkIn;
  const checkOut =
    searchState?.calendarDateLeave ??
    searchPayload?.checkOut ??
    searchPayload?.searchPhrase?.checkOut;
  if (!checkIn || !checkOut) return null;
  try {
    const parse = (value) => {
      const parts = String(value).split("/");
      if (parts.length < 3) return null;
      return new Date(parts[2], parts[1] - 1, parts[0]);
    };
    const inDate = parse(checkIn);
    const outDate = parse(checkOut);
    if (!inDate || !outDate) return null;
    const nights = Math.floor((outDate - inDate) / (1000 * 60 * 60 * 24));
    return nights >= 0 ? nights : null;
  } catch {
    return null;
  }
}

/**
 * @param {{
 *   searchState?: { roomInfo?: { rooms?: object[] }, calendarDateArrival?: string, calendarDateLeave?: string },
 *   searchPayload?: { searchPhrase?: { rooms?: object[], checkIn?: string, checkOut?: string }, rooms?: object[], checkIn?: string, checkOut?: string },
 *   result?: object|null,
 *   resultHotelName?: string|null,
 *   traceContext?: object,
 *   trace?: boolean,
 * }} input
 */
export function resolveHotelSearchOccupancy(input = {}) {
  const {
    searchState = {},
    searchPayload = {},
    result = null,
    resultHotelName = null,
    traceContext = {},
    trace = true,
  } = input;

  const { rooms, sourceUsedForDisplay } = pickRoomsSource(searchState, searchPayload);
  const totals = sumRoomsOccupancy(rooms);
  const nights = computeNights(searchPayload, searchState);

  const resultOverride =
    result?._searchOccupancyOverride ??
    result?.searchOccupancyOverride ??
    null;

  let cardDisplayedAdults = totals.adults;
  let cardDisplayedChildren = totals.children;
  let cardDisplayedRooms = totals.rooms;
  let displaySource = sourceUsedForDisplay;

  if (
    resultOverride &&
    typeof resultOverride === "object" &&
    resultOverride.explicit === true
  ) {
    cardDisplayedAdults = finiteNonNegative(resultOverride.adults) ?? cardDisplayedAdults;
    cardDisplayedChildren =
      finiteNonNegative(resultOverride.children) ?? cardDisplayedChildren;
    cardDisplayedRooms = finiteNonNegative(resultOverride.rooms) ?? cardDisplayedRooms;
    displaySource = "result.explicitOccupancyOverride";
  }

  const mismatchDetected =
    totals.rooms > 0 &&
    (cardDisplayedAdults !== totals.adults ||
      cardDisplayedChildren !== totals.children ||
      cardDisplayedRooms !== totals.rooms);

  const tracePayload = {
    searchAdults: totals.adults,
    searchChildren: totals.children,
    searchRooms: totals.rooms,
    childrenAges: totals.childrenAges,
    payloadSentToSupplier: {
      rooms,
      source: sourceUsedForDisplay,
    },
    cardDisplayedAdults,
    cardDisplayedChildren,
    cardDisplayedRooms,
    resultHotelName: resultHotelName ?? result?.DisplayName ?? result?.HotelName ?? null,
    sourceUsedForDisplay: displaySource,
    mismatchDetected,
    nights,
    passengers: cardDisplayedAdults + cardDisplayedChildren,
    ...traceContext,
  };

  if (trace) {
    emitTrace("[HOTEL_SEARCH_OCCUPANCY_BUILD_TRACE]", tracePayload);
    emitTrace("[HOTEL_RESULT_OCCUPANCY_DISPLAY_TRACE]", tracePayload);
    if (mismatchDetected) {
      emitTrace("[HOTEL_OCCUPANCY_MISMATCH_TRACE]", tracePayload);
    }
  }

  return {
    ...totals,
    nights,
    cardDisplayedAdults,
    cardDisplayedChildren,
    cardDisplayedRooms,
    sourceUsedForDisplay: displaySource,
    mismatchDetected,
    roomsSource: rooms,
  };
}

export function countHotelSearchPassengersFromPhrase(searchPhrase) {
  const totals = sumRoomsOccupancy(searchPhrase?.rooms);
  return {
    rooms: totals.rooms,
    passengers: totals.passengers,
    adults: totals.adults,
    children: totals.children,
  };
}
